import { parse } from 'date-fns'
import { formatDateCustom, parseDateCustom } from '../../../../common/utils/DateUtils'
import { checkRankConsistency, RANK_CONSISTENCY_ERROR_MESSAGE } from './validation'

let refDate = null

/**
 * 日付文字列をDateオブジェクトにパース
 *
 * @function _parseDate
 * @param {string} date - パースする日付文字列（yyyy/MM/dd形式）
 * @returns {Date | null} パースされたDateオブジェクト、または入力がない場合null
 */
export const _parseDate = (date) => {
  if (refDate === null) refDate = new Date()
  return !date ? null : parse(date, 'yyyy/MM/dd', refDate)
}

/**
 * 文字列を5桁にパディング
 *
 * @example
 *   padString(1) // '00001'
 *   padString('42') // '00042'
 *
 * @function padString
 * @param {string | number} str - パディングする文字列または数値
 * @param {string} [padChar='0'] - パディングに使用する文字（デフォルト: '0'）. Default is `'0'`
 * @returns {string} 5桁にパディングされた文字列
 */
export const padString = (str, padChar = '0') => String(str).padStart(5, padChar)

/**
 * Redux状態更新後に処理を実行するためのヘルパー Reduxの状態更新は同期的だが、React再レンダリングを確実に待つため requestAnimationFrameとsetTimeoutを組み合わせている
 *
 * @function afterStateUpdate
 * @param {Function} callback - 実行するコールバック関数
 */
export const afterStateUpdate = (callback) => {
  requestAnimationFrame(() => {
    setTimeout(callback, 0)
  })
}

/**
 * 区分単位エラー（errors.byClassification）の解消判定と除去を行う（バグ #3293） データ変更操作（モーダルでの追加・編集、行の削除・復帰、インポート）の後に呼び出す。
 *
 * - ランク整合性エラー: 変更後のレコードで再判定し、整合した場合のみ除去する（不整合のままなら残す）
 * - それ以外（サーバー側チェック由来のエラー）: 画面側では解消判定できないため、 データ変更時点で除去し、次回登録時のサーバー再チェックに委ねる
 *
 * @function refreshClassificationErrors
 * @param {Object} store - 画面用Reduxストア
 * @param {Function} dispatch - Dispatch(type, payload) 形式のディスパッチ関数
 * @param {string} classificationCode - 対象の区分コード
 */
export const refreshClassificationErrors = (store, dispatch, classificationCode) => {
  const { errors, userClassificationRecords } = store.getState()
  const currentErrors = errors?.byClassification?.[classificationCode]
  if (!currentErrors || currentErrors.length === 0) return

  const records = userClassificationRecords?.[classificationCode] || []
  const isRankConsistent = checkRankConsistency(records)

  // ランク整合性エラーは不整合が続いている場合のみ残し、その他のエラーは除去する
  const remainingErrors = currentErrors.filter(
    (message) => message === RANK_CONSISTENCY_ERROR_MESSAGE && !isRankConsistent
  )

  // 除去対象がない場合は状態を更新しない
  if (remainingErrors.length === currentErrors.length) return

  const newErrorsByClassification = { ...errors.byClassification }
  if (remainingErrors.length === 0) {
    delete newErrorsByClassification[classificationCode]
  } else {
    newErrorsByClassification[classificationCode] = remainingErrors
  }
  dispatch('SET_STATE', {
    path: 'errors.byClassification',
    value: newErrorsByClassification
  })
}

// ========================================
// データ変換・正規化関数
// ========================================

/**
 * 値を正規化（undefined、null、空文字列を同じものとして扱う）
 *
 * @function normalizeValue
 * @param {any} val - 正規化する値
 * @returns {string} 正規化された文字列
 */
export const normalizeValue = (val) => {
  if (val === null || val === undefined || val === '') return ''
  return String(val)
}

/**
 * 数値を正規化（null、undefinedの場合はundefinedを返す）
 *
 * @function normalizeNumber
 * @param {number | null | undefined} val - 正規化する数値
 * @returns {number | undefined} 正規化された数値またはundefined
 */
export const normalizeNumber = (val) => {
  if (val === null || val === undefined) return undefined
  return val
}

/**
 * 日付を正規化（YYYY/MM/DD形式の文字列に変換）
 *
 * @function normalizeDate
 * @param {Date | string | null | undefined} date - 正規化する日付（Dateオブジェクトまたは文字列）
 * @returns {string | null} YYYY/MM/DD形式の文字列またはnull
 */
export const normalizeDate = (date) => {
  if (!date) return null

  // Dateオブジェクトの場合
  if (date instanceof Date) {
    return formatDateCustom(date, 'yyyy/MM/dd')
  }

  // 文字列の場合（yyyy/MM/dd形式を想定）
  if (typeof date === 'string' && date.trim() !== '') {
    try {
      // 既にyyyy/MM/dd形式の文字列であることを確認し、正規化
      const parsed = parseDateCustom(date, 'yyyy/MM/dd')
      return parsed ? formatDateCustom(parsed, 'yyyy/MM/dd') : null
    } catch {
      return null
    }
  }

  return null
}

/**
 * 日付がInvalid Date（パース失敗のDateオブジェクト）かどうか判定
 *
 * @function isInvalidDate
 * @param {unknown} date - 判定する値
 * @returns {boolean} Invalid Dateの場合true
 */
export const isInvalidDate = (date) => date instanceof Date && isNaN(date.getTime())

/** インポートで受け付ける日付形式（CSV/Excel共通）。月日の0埋めなし（1999/4/1等）はdate-fnsが許容する */
const IMPORT_DATE_FORMATS = ['yyyy-MM-dd', 'yyyy/MM/dd']

/**
 * インポートファイルの日付文字列をDateオブジェクトにパースする。
 *
 * CSV/ExcelともYYYY-MM-DD / YYYY/MM/DDの2形式のみを受け付ける（0埋めなし可）。 Excelの日付型セルは「セルの表示形式に従った文字列」で届くため、表示形式が上記2形式
 * （またはYYYY/MM/DDへ変換される既定の日付書式等）であれば取り込める。日時書式（時刻付き）は対象外。
 *
 * @function parseImportDate
 * @param {unknown} value - インポートファイルのセル値
 * @returns {Date | null} パース結果。空値はnull、どの形式でも解釈できない場合はInvalid Date （既存のエラー処理＝スキーマエラー→日本語メッセージ変換・元の値のセル表示に委ねる）
 */
export const parseImportDate = (value) => {
  if (value == null || value === '') return null
  const text = String(value).trim()
  if (text === '') return null

  // date-fnsのyyyyは1〜4桁の年を許容するため（例: '9/1/26'が9年1月26日として成立）、
  // 先頭が4桁の年であることを事前に確認する（2桁年の書式などを確実に除外する）
  if (!/^\d{4}(\D|$)/.test(text)) {
    return new Date(NaN)
  }

  for (const format of IMPORT_DATE_FORMATS) {
    try {
      const parsed = parseDateCustom(text, format)
      if (parsed instanceof Date && !isNaN(parsed.getTime())) return parsed
    } catch {
      // この形式では解釈できないため次の形式を試す
    }
  }

  return new Date(NaN)
}

/**
 * レコードに変更があるかチェック
 *
 * @function hasRecordChanged
 * @param {Object} record - チェックするレコード
 * @param {Object} initialRecord - 初期状態のレコード
 * @returns {boolean} 変更がある場合true
 */
export const hasRecordChanged = (record, initialRecord) => {
  if (!initialRecord) return false

  return (
    normalizeValue(record.value) !== normalizeValue(initialRecord.value) ||
    normalizeValue(record.code) !== normalizeValue(initialRecord.code) ||
    normalizeNumber(record.rank) !== normalizeNumber(initialRecord.rank) ||
    normalizeValue(record.displayOrder) !== normalizeValue(initialRecord.displayOrder) ||
    normalizeDate(record.validStartDate) !== normalizeDate(initialRecord.validStartDate) ||
    normalizeDate(record.validEndDate) !== normalizeDate(initialRecord.validEndDate) ||
    normalizeValue(record.memo) !== normalizeValue(initialRecord.memo)
  )
}

/**
 * 変更されたレコードを抽出
 *
 * @function extractChangedRecords
 * @param {Array} records - チェックするレコード配列
 * @param {Map} initialRecordsMap - 初期状態のレコードマップ（key -> record）
 * @returns {Array} 変更されたレコードの配列
 */
export const extractChangedRecords = (records, initialRecordsMap) => {
  return records.filter((record) => {
    // 新規作成された項目
    if (record.isCreated) return true
    // 削除された項目
    if (record.isDeleted) return true

    // 初期状態と比較して変更があった項目
    const initialRecord = initialRecordsMap.get(record.key)
    return hasRecordChanged(record, initialRecord)
  })
}

/**
 * 表示順を再採番したマップを作成
 *
 * @function createDisplayOrderMap
 * @param {Array} records - レコード配列
 * @returns {Map} 表示順マップ（key -> displayOrder）
 */
export const createDisplayOrderMap = (records) => {
  // 削除されていないレコードのみを取得してソート
  const activeRecords = records
    .filter((record) => !record.isDeleted)
    .sort((a, b) => {
      const orderA = parseInt(a.displayOrder, 10) || 0
      const orderB = parseInt(b.displayOrder, 10) || 0
      return orderA - orderB
    })

  // 1から連番でdisplay_orderを再採番したマップを作成
  const displayOrderMap = new Map()
  activeRecords.forEach((record, index) => {
    displayOrderMap.set(record.key, index + 1)
  })
  return displayOrderMap
}

/**
 * レコードをAPIリクエスト形式に変換
 *
 * @function convertRecordToApiFormat
 * @param {Object} record - 変換するレコード
 * @param {Map} displayOrderMap - 表示順マップ
 * @returns {Object} APIリクエスト形式のレコード
 */
export const convertRecordToApiFormat = (record, displayOrderMap) => {
  // transaction_typeを決定
  let transactionType = 'update'
  if (record.isCreated) {
    transactionType = 'create'
  } else if (record.isDeleted) {
    transactionType = 'delete'
  } else if (record.isSkipOnly) {
    transactionType = 'skip'
  }

  // 日付をYYYY/MM/DD形式に変換
  // インポート由来の不正日付（Invalid Date）はtruthyなため!dateを通過し、
  // date-fnsのformatがRangeErrorを投げる。空文字に落として保護する（バグ #3230/#3231関連）
  const formatDate = (date) => {
    if (!date) return ''
    if (isInvalidDate(date)) return ''
    return formatDateCustom(date, 'yyyy/MM/dd')
  }

  // display_orderを再採番（削除されるレコードは元の値を保持）
  const displayOrder = record.isDeleted
    ? parseInt(record.displayOrder, 10)
    : displayOrderMap.get(record.key) || parseInt(record.displayOrder, 10)

  return {
    key: transactionType === 'create' ? '' : record.key,
    value: record.value,
    code: record.code || '',
    display_order: displayOrder,
    rank: record.rank != null ? record.rank : null,
    valid_start_date: formatDate(record.validStartDate),
    valid_end_date: formatDate(record.validEndDate),
    memo: record.memo || '',
    transaction_type: transactionType
  }
}

/**
 * 配列内の要素を移動する（@dnd-kit/sortable の arrayMove 相当）
 *
 * @param {Array} array
 * @param {number} fromIndex
 * @param {number} toIndex
 * @returns {Array}
 */
const moveArrayItem = (array, fromIndex, toIndex) => {
  const next = array.slice()
  const [removed] = next.splice(fromIndex, 1)
  next.splice(toIndex, 0, removed)
  return next
}

/**
 * フィルター表示中の並び替え結果を全件リストへマージする。 非表示レコードは元のスロット位置・内容を保持し、表示中レコードのみ入れ替える。 （検索／有効期限フィルター適用中のドラッグで非表示行が消失する不具合の再発防止用）
 *
 * @function reorderVisibleRecordsPreservingHidden
 * @param {Object} params
 * @param {Object[]} params.sortedRecords - DisplayOrder 順の全件
 * @param {Object[]} params.filteredRecords - 現在表示中のレコード
 * @param {number} params.oldIndex - 表示リスト上の移動元 index
 * @param {number} params.newIndex - 表示リスト上の移動先 index
 * @returns {Object[] | null} DisplayOrder 再採番済みの全件。index 不正時は null
 */
export const reorderVisibleRecordsPreservingHidden = ({ sortedRecords, filteredRecords, oldIndex, newIndex }) => {
  if (
    !Array.isArray(sortedRecords) ||
    !Array.isArray(filteredRecords) ||
    oldIndex < 0 ||
    newIndex < 0 ||
    oldIndex >= filteredRecords.length ||
    newIndex >= filteredRecords.length
  ) {
    return null
  }

  const reorderedVisible = moveArrayItem(filteredRecords, oldIndex, newIndex)
  const visibleKeys = new Set(filteredRecords.map((record) => record.key))
  let visibleCursor = 0
  const mergedRecords = sortedRecords.map((record) =>
    visibleKeys.has(record.key) ? reorderedVisible[visibleCursor++] : record
  )

  return mergedRecords.map((record, index) => ({
    ...record,
    displayOrder: padString(index)
  }))
}
